import { useState } from 'react';
import Papa from 'papaparse';
import { useDropzone } from 'react-dropzone';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Select } from '@/components/Select';
import { Upload, FileText, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { leadsService, ImportLeadsResult } from '@/services/leads.service';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const TARGET_FIELDS = [
  { value: '__skip__', label: '— Skip —' },
  { value: 'phone', label: 'Phone' },
  { value: 'profileName', label: 'Name' },
  { value: 'email', label: 'Email' },
  { value: 'notes', label: 'Notes' },
  { value: 'status', label: 'Status (new/contacted/qualified/won/lost)' },
];

const MAX_ROWS_PER_REQUEST = 2000;

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called after a successful import so the inbox can reload. */
  onImported: () => void;
}

export function LeadImportModal({ open, onClose, onImported }: Props) {
  const [filename, setFilename] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportLeadsResult | null>(null);

  const reset = () => {
    setFilename(''); setHeaders([]); setRows([]); setMapping({}); setResult(null);
  };

  const close = () => {
    if (importing) return;
    reset();
    onClose();
  };

  const onDrop = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    setFilename(file.name);
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        setHeaders(res.meta.fields ?? []);
        setRows(res.data);
        const initial = Object.fromEntries(
          (res.meta.fields ?? []).map((h) => {
            const lower = h.toLowerCase();
            if (lower.includes('phone') || lower.includes('mobile') || lower.includes('whatsapp')) return [h, 'phone'];
            if (lower.includes('name')) return [h, 'profileName'];
            if (lower.includes('email')) return [h, 'email'];
            if (lower.includes('note')) return [h, 'notes'];
            if (lower.includes('status')) return [h, 'status'];
            return [h, '__skip__'];
          }),
        );
        setMapping(initial);
      },
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'] },
    maxFiles: 1,
  });

  const identityMapped = Object.values(mapping).some(field => field === 'phone' || field === 'email');
  const previewRows = rows.slice(0, 5);
  const mappedHeaders = headers.filter((h) => mapping[h] && mapping[h] !== '__skip__');

  const submit = async () => {
    setImporting(true);
    try {
      // Backend caps at 2000 rows per request — chunk larger files and merge.
      const merged: ImportLeadsResult = { created: 0, skipped: 0, errors: [] };
      for (let i = 0; i < rows.length; i += MAX_ROWS_PER_REQUEST) {
        const r = await leadsService.importCsv({
          filename,
          mapping,
          rows: rows.slice(i, i + MAX_ROWS_PER_REQUEST),
        });
        merged.created += r.created;
        merged.skipped += r.skipped;
        merged.errors = merged.errors.concat(r.errors).slice(0, 100);
      }
      setResult(merged);
      toast.success(`Imported ${merged.created} lead${merged.created === 1 ? '' : 's'} (${merged.skipped} skipped, ${merged.errors.length} error${merged.errors.length === 1 ? '' : 's'})`);
      onImported();
    } catch (e: unknown) {
      if (!(e as { response?: unknown })?.response) toast.error('Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Modal open={open} onClose={close} title="Import leads from CSV" widthClass="max-w-2xl">
      {/* Step 1 — pick a file */}
      {!filename && (
        <div
          {...getRootProps()}
          className={clsx(
            'rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-all',
            isDragActive
              ? 'border-primary bg-primary-soft'
              : 'border-border bg-surface hover:border-border-strong hover:bg-surface-alt',
          )}
        >
          <input {...getInputProps()} />
          <div className="flex h-12 w-12 mx-auto mb-3 items-center justify-center rounded-2xl bg-primary-soft text-primary">
            <Upload size={20} />
          </div>
          <p className="font-semibold text-text-primary text-sm">
            {isDragActive ? 'Drop the file here' : 'Drop a CSV file here or click to upload'}
          </p>
          <p className="mt-1 text-xs text-text-muted">Map a phone or email column. Matching leads are reused and linked to contacts.</p>
        </div>
      )}

      {/* Step 2 — map columns + preview */}
      {filename && !result && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 rounded-xl border border-border bg-surface-alt px-3.5 py-2.5">
            <FileText size={16} className="shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-text-primary">{filename}</p>
              <p className="text-[11px] text-text-muted">{rows.length.toLocaleString()} rows detected</p>
            </div>
            <button
              onClick={reset}
              className="shrink-0 text-xs text-text-muted hover:text-text-primary transition-colors"
            >
              Change file
            </button>
          </div>

          {/* Mapping */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">Map columns</p>
            <div className="divide-y divide-border rounded-xl border border-border">
              {headers.map((h) => (
                <div key={h} className="grid grid-cols-2 items-center gap-3 px-3.5 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-[13px] font-medium text-text-primary">{h}</p>
                    <p className="text-[10.5px] text-text-muted">CSV column</p>
                  </div>
                  <Select
                    value={mapping[h] ?? '__skip__'}
                    onChange={(e) => setMapping((m) => ({ ...m, [h]: e.target.value }))}
                  >
                    {TARGET_FIELDS.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </Select>
                </div>
              ))}
            </div>
          </div>

          {/* Preview */}
          {mappedHeaders.length > 0 && previewRows.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
                Preview (first {previewRows.length} rows)
              </p>
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-border bg-surface-alt">
                      {mappedHeaders.map((h) => (
                        <th key={h} className="px-3 py-2 font-semibold text-text-secondary whitespace-nowrap">
                          {TARGET_FIELDS.find((f) => f.value === mapping[h])?.label ?? mapping[h]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {previewRows.map((r, i) => (
                      <tr key={i}>
                        {mappedHeaders.map((h) => (
                          <td key={h} className="px-3 py-2 text-text-primary whitespace-nowrap max-w-[180px] truncate">
                            {r[h] ?? ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!identityMapped && (
            <div className="flex items-center gap-2 rounded-xl border border-danger/30 bg-danger-soft px-3.5 py-2.5 text-xs text-danger">
              <AlertTriangle size={14} className="shrink-0" />
              Map at least one column to Phone or Email before importing.
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" size="sm" onClick={close}>Cancel</Button>
            <Button size="sm" disabled={!identityMapped || rows.length === 0} loading={importing} onClick={submit}>
              Import {rows.length.toLocaleString()} row{rows.length === 1 ? '' : 's'}
            </Button>
          </div>
        </div>
      )}

      {/* Step 3 — result summary */}
      {result && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 rounded-xl border border-success/30 bg-success-soft px-3.5 py-3">
            <CheckCircle2 size={16} className="shrink-0 text-success" />
            <p className="text-sm font-medium text-success">Import complete.</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl border border-border bg-surface-alt p-3 text-center">
              <div className="text-xl font-bold text-success tabular-nums">{result.created.toLocaleString()}</div>
              <div className="mt-0.5 text-[10px] uppercase tracking-wide text-text-muted">Created</div>
            </div>
            <div className="rounded-xl border border-border bg-surface-alt p-3 text-center">
              <div className="text-xl font-bold text-text-muted tabular-nums">{result.skipped.toLocaleString()}</div>
              <div className="mt-0.5 text-[10px] uppercase tracking-wide text-text-muted">Skipped dupes</div>
            </div>
            <div className="rounded-xl border border-border bg-surface-alt p-3 text-center">
              <div className={clsx('text-xl font-bold tabular-nums', result.errors.length > 0 ? 'text-danger' : 'text-text-muted')}>
                {result.errors.length.toLocaleString()}
              </div>
              <div className="mt-0.5 text-[10px] uppercase tracking-wide text-text-muted">Errors</div>
            </div>
          </div>

          {result.errors.length > 0 && (
            <div className="max-h-40 overflow-y-auto rounded-xl border border-border">
              {result.errors.map((e, i) => (
                <div key={i} className="flex items-center gap-2 border-b border-border px-3 py-1.5 text-xs last:border-0">
                  <span className="shrink-0 font-mono text-text-muted">Row {e.row}</span>
                  <span className="truncate text-danger">{e.reason}</span>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end pt-1">
            <Button size="sm" onClick={close}>Done</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
